import { useEffect, useMemo, useState } from "react"
import type { AppShell } from "@/App"
import { getAppConfig, type OrderRow } from "@/lib/api"

/** Espelho de functions/lib/engine/orders.ts:DEFAULT_ORDER_SOURCES. */
export const DEFAULT_ORDER_SOURCES: Record<string, { name: string; email?: string }> = {
  "4": { name: "ONLINE", email: "online@electrominor.pt" },
  "1": { name: "BALCÃO", email: "armazem@electrominor.pt" },
  "2": { name: "COMERCIAL", email: "comercial@electrominor.pt" },
  "3": { name: "EXPORTAÇÃO", email: "comercial@electrominor.pt" },
}
export const ONLINE_SECTION = "4"

export interface SourceOption {
  section: string
  name: string
}

/** Fontes (setting `order_sources`) + nome da fonte de uma encomenda. */
export function useOrderSources(shell: AppShell | null) {
  const [map, setMap] = useState<Record<string, { name?: string }>>(DEFAULT_ORDER_SOURCES)
  useEffect(() => {
    if (!shell) return
    getAppConfig(shell)
      .then((cfg) => {
        let v = cfg.config.order_sources ?? cfg.schema.order_sources?.default
        if (typeof v === "string") {
          try {
            v = JSON.parse(v)
          } catch {
            v = null
          }
        }
        if (v && typeof v === "object") setMap(v as Record<string, { name?: string }>)
      })
      .catch(() => {})
  }, [shell])

  return useMemo(() => {
    const sectionOf = (o: Pick<OrderRow, "source_section">) =>
      String(o.source_section ?? ONLINE_SECTION).trim() || ONLINE_SECTION
    const nameOf = (section: string) =>
      String(map[section]?.name ?? DEFAULT_ORDER_SOURCES[section]?.name ?? `SECÇÃO ${section}`).toUpperCase()
    const options: SourceOption[] = Object.keys(map)
      .sort()
      .map((section) => ({ section, name: nameOf(section) }))
    return {
      options,
      sectionOf,
      // Online feita à mão no ETICDATA (orçamento / comercial) distingue-se da do Shopify.
      sourceName: (o: Pick<OrderRow, "source_section"> & Partial<Pick<OrderRow, "shopify_order_id" | "erp_origin_ref">>) => {
        const section = sectionOf(o)
        const base = nameOf(section)
        const erp = String(o.shopify_order_id ?? "").startsWith("etic:")
        return erp && section === ONLINE_SECTION ? `${base} · ${o.erp_origin_ref ? "ORÇAMENTO" : "MANUAL"}` : base
      },
    }
  }, [map])
}
